import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextAreaField } from 'components/Field/TextAreaField/TextAreaField';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { UI_DATE_FORMAT_LONG } from 'lib/constants';
import { formatDate } from 'lib/riskbase';
import { smartText } from 'lib/str';
import { cloneDeep, isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, doAseSetFlagResult } from 'store/actions';
import {
  selectAseFlagResult,
  selectAseTplFlag,
} from 'store/assessments/selectors';

export const FlagUnsetModal: React.FC<
  ModalScreenProps<ModalScreens.AseFlagUnset>
> = ({ route, navigation }) => {
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();

  const { flagId } = route.params;

  const flag = useSelector((state) => selectAseTplFlag(state, flagId));
  const flagResult = useSelector((state) => selectAseFlagResult(state, flagId));

  const forceUpdate = useForceUpdate();

  const initValues = React.useRef({
    comment: '',
  });
  const changes = React.useRef(cloneDeep(initValues.current));
  const checkIsDirty = React.useCallback(
    () => !isEqual(initValues.current, changes.current),
    [],
  );
  const isDirty = React.useRef<boolean>(false);
  isDirty.current = checkIsDirty();

  const handleChangeWithName = React.useCallback(
    <
      N extends keyof typeof changes.current,
      V extends (typeof changes.current)[N],
    >(
      name: N,
      value: V,
    ) => {
      changes.current[name] = value;
      if (isDirty.current !== checkIsDirty()) {
        forceUpdate();
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    dispatch(
      doAseSetFlagResult({
        flagId,
        comment: changes.current.comment,
        doDelete: true,
      }),
    );
  }, [dispatch, flagId]);

  const handleClose = React.useCallback(() => {
    if (isDirty.current) {
      confirmUnsaved({
        onSave: () => handleSave,
        onDiscard: () => dispatch(NAVIGATE_MODAL_BACK()),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, handleSave, dispatch]);

  const isDirtyVar = isDirty.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  if (!flag) {
    return null;
  }

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="Unset Flag"
          cancelButtonText="Close"
          saveButtonText={flagResult ? 'Unset Flag' : 'Set Flag'}
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card marginBottomQuarter>
        <CardRow label={flag.name} value={flag.description} labelAlignTop />
      </Card>
      <Card explanation={smartText("Press 'Unset Flag' to confirm")}>
        <TextAreaField
          name="comment"
          placeholder="Optional Unset Comment"
          onChangeWithName={handleChangeWithName}
          onEsc={handleClose}
          autoFocusWeb
          autoFocusDelay
        />
      </Card>

      {flagResult && (
        <Card label="Current Details">
          <CardRow
            value={formatDate(flagResult.timestamp, UI_DATE_FORMAT_LONG)}
          />
          <CardRow value={`${flagResult.by.name} (${flagResult.by.email})`} />
          <CardRow label="Comment" value={flagResult.comment} labelAlignTop />
        </Card>
      )}
    </PageScrollView>
  );
};
